import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { People, Delete, PersonAdd, Email, CalendarToday, Person, Lock, Visibility, VisibilityOff, Phone } from '@mui/icons-material';
import { getAdmins, revokeAdmin, updateProfile, changePassword } from '../../services/api';
import { useAuth } from '../context/AuthContext';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const PRIMARY_HOVER = '#5a8499';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

/* ── Personal Tab Component ─────────────────────────────────────── */
function PersonalTab({ currentAdmin, setAdmin }) {
  const [name, setName] = useState(currentAdmin?.name || '');
  const [email, setEmail] = useState(currentAdmin?.email || '');
  const [phone, setPhone] = useState(currentAdmin?.phone || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (currentAdmin) {
      setName(currentAdmin.name || '');
      setEmail(currentAdmin.email || '');
      setPhone(currentAdmin.phone || '');
    }
  }, [currentAdmin]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileLoading(true);
    try {
      const result = await updateProfile({ name, email, phone });
      setProfileSuccess('Profile updated successfully');
      // Update auth context with new data
      if (result?.data) {
        setAdmin(prev => ({ ...prev, ...result.data }));
      }
    } catch (err) {
      setProfileError(err.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword({ currentPassword, newPassword, confirmPassword });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: RADIUS,
      fontFamily: FONT_BODY,
      '&.Mui-focused fieldset': { borderColor: PRIMARY },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      {/* Profile Update Section */}
      <Paper
        component="form"
        onSubmit={handleProfileSubmit}
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: RADIUS,
          boxShadow: CARD_SHADOW,
          border: '2px solid rgba(108, 152, 173, 0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Person sx={{ color: PRIMARY }} />
          <Typography variant="h6" sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
            Profile Information
          </Typography>
        </Box>

        {profileError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>
            {profileError}
          </Alert>
        )}
        {profileSuccess && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: RADIUS }}>
            {profileSuccess}
          </Alert>
        )}

        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          sx={{ ...textFieldSx, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person sx={{ color: PRIMARY, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          sx={{ ...textFieldSx, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email sx={{ color: PRIMARY, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Contact Number"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          sx={{ ...textFieldSx, mb: 2.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone sx={{ color: PRIMARY, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={profileLoading}
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: RADIUS,
            px: 4,
            py: 1.2,
            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_HOVER} 100%)`,
            boxShadow: '0 4px 14px rgba(108, 152, 173, 0.35)',
            transition: TRANSITION,
            '&:hover': {
              background: `linear-gradient(135deg, ${PRIMARY_HOVER} 0%, ${PRIMARY} 100%)`,
              boxShadow: '0 6px 20px rgba(108, 152, 173, 0.45)',
            },
          }}
        >
          {profileLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Update Profile'}
        </Button>
      </Paper>

      {/* Password Change Section */}
      <Paper
        component="form"
        onSubmit={handlePasswordSubmit}
        elevation={0}
        sx={{
          p: 3,
          borderRadius: RADIUS,
          boxShadow: CARD_SHADOW,
          border: '2px solid rgba(108, 152, 173, 0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Lock sx={{ color: PRIMARY }} />
          <Typography variant="h6" sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
            Change Password
          </Typography>
        </Box>

        {passwordError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>
            {passwordError}
          </Alert>
        )}
        {passwordSuccess && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: RADIUS }}>
            {passwordSuccess}
          </Alert>
        )}

        <TextField
          label="Current Password"
          type={showCurrentPassword ? 'text' : 'password'}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          fullWidth
          required
          sx={{ ...textFieldSx, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: PRIMARY, fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" size="small">
                  {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="New Password"
          type={showNewPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          fullWidth
          required
          sx={{ ...textFieldSx, mb: 2 }}
          helperText="Minimum 8 characters"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: PRIMARY, fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                  {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Confirm New Password"
          type={showNewPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          required
          sx={{ ...textFieldSx, mb: 2.5 }}
          error={confirmPassword && newPassword !== confirmPassword}
          helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: PRIMARY, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={passwordLoading}
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: RADIUS,
            px: 4,
            py: 1.2,
            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_HOVER} 100%)`,
            boxShadow: '0 4px 14px rgba(108, 152, 173, 0.35)',
            transition: TRANSITION,
            '&:hover': {
              background: `linear-gradient(135deg, ${PRIMARY_HOVER} 0%, ${PRIMARY} 100%)`,
              boxShadow: '0 6px 20px rgba(108, 152, 173, 0.45)',
            },
          }}
        >
          {passwordLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Change Password'}
        </Button>
      </Paper>
    </Box>
  );
}

/* ── Admin Manager Tab Component ────────────────────────────────── */
function AdminManagerTab({ currentAdmin }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [adminToRevoke, setAdminToRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState('');
  const navigate = useNavigate();

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdmins();
      setAdmins(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleRevokeClick = (admin) => {
    setAdminToRevoke(admin);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!adminToRevoke) return;
    setRevoking(true);
    try {
      await revokeAdmin(adminToRevoke.id);
      setRevokeDialogOpen(false);
      setAdminToRevoke(null);
      setConfirmationInput('');
      fetchAdmins();
    } catch (err) {
      setError(err.message || 'Failed to revoke admin');
    } finally {
      setRevoking(false);
    }
  };

  const handleRevokeCancel = () => {
    setRevokeDialogOpen(false);
    setAdminToRevoke(null);
    setConfirmationInput('');
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusChip = (status) => {
    const isActive = status === 'ACTIVE';
    return (
      <Chip
        label={isActive ? 'Active' : 'Pending'}
        size="small"
        sx={{
          fontFamily: FONT_BODY,
          fontWeight: 600,
          fontSize: '0.75rem',
          backgroundColor: isActive ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 152, 0, 0.15)',
          color: isActive ? '#2e7d32' : '#f57c00',
          border: `1px solid ${isActive ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
        }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => navigate('/admin/accounts/invite')}
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: RADIUS,
            px: 3,
            py: 1,
            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_HOVER} 100%)`,
            boxShadow: '0 4px 14px rgba(108, 152, 173, 0.35)',
            transition: TRANSITION,
            '&:hover': {
              background: `linear-gradient(135deg, ${PRIMARY_HOVER} 0%, ${PRIMARY} 100%)`,
              boxShadow: '0 6px 20px rgba(108, 152, 173, 0.45)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          Invite Admin
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: RADIUS,
            boxShadow: CARD_SHADOW,
            border: '2px solid rgba(108, 152, 173, 0.1)',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(108, 152, 173, 0.06)' }}>
                <TableCell sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
                  Email
                </TableCell>
                <TableCell sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
                  Contact Number
                </TableCell>
                <TableCell sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
                  Joined
                </TableCell>
                <TableCell align="center" sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT_BODY, color: '#666' }}>
                    No admin users found
                  </TableCell>
                </TableRow>
              ) : (
                admins.map((admin) => {
                  const isCurrentUser = currentAdmin?.id === admin.id;
                  return (
                    <TableRow
                      key={admin.id}
                      sx={{
                        transition: TRANSITION,
                        backgroundColor: isCurrentUser ? 'rgba(108, 152, 173, 0.05)' : 'transparent',
                        '&:hover': { backgroundColor: 'rgba(108, 152, 173, 0.08)' },
                      }}
                    >
                      <TableCell sx={{ fontFamily: FONT_BODY, fontWeight: 500, color: SECONDARY }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {admin.name}
                          {isCurrentUser && (
                            <Chip
                              label="You"
                              size="small"
                              sx={{
                                fontFamily: FONT_BODY,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: PRIMARY,
                                color: '#fff',
                                height: 20,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: FONT_BODY, color: '#666' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email sx={{ fontSize: 16, color: PRIMARY }} />
                          {admin.email}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: FONT_BODY, color: '#666' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone sx={{ fontSize: 16, color: PRIMARY }} />
                          {admin.phone || '—'}
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(admin.status)}</TableCell>
                      <TableCell sx={{ fontFamily: FONT_BODY, color: '#666' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday sx={{ fontSize: 16, color: PRIMARY }} />
                          {formatDate(admin.createdAt)}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {isCurrentUser ? (
                          <Tooltip title="You cannot revoke your own account">
                            <span>
                              <IconButton disabled size="small">
                                <Delete sx={{ fontSize: 20 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Revoke access">
                            <IconButton
                              size="small"
                              onClick={() => handleRevokeClick(admin)}
                              sx={{
                                color: '#f44336',
                                transition: TRANSITION,
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                  transform: 'scale(1.1)',
                                },
                              }}
                            >
                              <Delete sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={handleRevokeCancel}
        PaperProps={{
          sx: {
            borderRadius: RADIUS,
            boxShadow: CARD_SHADOW,
            minWidth: 450,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: SECONDARY }}>
          Revoke Admin Access
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_BODY, mb: 2 }}>
            Are you sure you want to revoke access for <strong>{adminToRevoke?.name}</strong> ({adminToRevoke?.email})?
            This action cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ fontFamily: FONT_BODY, mb: 1.5, fontSize: '0.9rem' }}>
            To confirm, type the user's email:
          </DialogContentText>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: '#666',
              wordBreak: 'break-all',
              userSelect: 'all',
            }}
          >
            {adminToRevoke?.email}
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter email to confirm"
            value={confirmationInput}
            onChange={(e) => setConfirmationInput(e.target.value)}
            autoComplete="off"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                borderRadius: '8px',
                '&.Mui-focused fieldset': { borderColor: '#f44336' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleRevokeCancel}
            disabled={revoking}
            sx={{
              fontFamily: FONT_BODY,
              textTransform: 'none',
              color: '#666',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRevokeConfirm}
            disabled={revoking || confirmationInput !== adminToRevoke?.email}
            variant="contained"
            sx={{
              fontFamily: FONT_BODY,
              textTransform: 'none',
              borderRadius: RADIUS,
              backgroundColor: '#f44336',
              '&:hover': { backgroundColor: '#d32f2f' },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(244, 67, 54, 0.3)',
                color: 'rgba(255, 255, 255, 0.6)',
              },
            }}
          >
            {revoking ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Revoke Access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ── Main Accounts Page Component ───────────────────────────────── */
export default function AccountsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const { admin: currentAdmin, setAdmin } = useAuth();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <People sx={{ fontSize: 32, color: PRIMARY }} />
        <Typography
          variant="h4"
          sx={{
            fontFamily: FONT_HEADING,
            fontWeight: 700,
            background: `linear-gradient(135deg, ${SECONDARY} 0%, ${PRIMARY} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Accounts
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: RADIUS,
          boxShadow: CARD_SHADOW,
          border: '2px solid rgba(108, 152, 173, 0.1)',
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              fontFamily: FONT_BODY,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.95rem',
              minHeight: 56,
              color: '#666',
              transition: TRANSITION,
              '&.Mui-selected': {
                color: PRIMARY,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: PRIMARY,
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab icon={<Person sx={{ fontSize: 20 }} />} iconPosition="start" label="Personal" />
          <Tab icon={<People sx={{ fontSize: 20 }} />} iconPosition="start" label="Admin Manager" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && <PersonalTab currentAdmin={currentAdmin} setAdmin={setAdmin} />}
      {activeTab === 1 && <AdminManagerTab currentAdmin={currentAdmin} />}
    </Box>
  );
}
